/**
 * Breadcrumbs — auto-generates a trail from the current URL path.
 * Reads the route segments from useLocation() and maps them to friendly
 * names via i18n. Each segment (except the last) is clickable.
 *
 * Detail pages may register a per-path label override via
 * useBreadcrumbOverride() — when an override exists for the cumulative
 * path of a segment, it replaces the auto-generated "#539" style label
 * with a human-readable code ("2026-01").
 */
import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, ChevronRight, ChevronLeft } from 'lucide-react'
import { useBreadcrumbContext } from './BreadcrumbContext'

// Map route segments to translation keys
const SEGMENT_LABELS: Record<string, { en: string; ar: string }> = {
  dashboard:      { en: 'Dashboard',      ar: 'الرئيسية' },
  projects:       { en: 'Projects',       ar: 'المشاريع' },
  tenders:        { en: 'Tenders',        ar: 'العطاءات' },
  companies:      { en: 'Companies',      ar: 'الشركات' },
  clients:        { en: 'Clients',        ar: 'العملاء' },
  consultants:    { en: 'Consultants',    ar: 'الاستشاريين' },
  suppliers:      { en: 'Suppliers',      ar: 'الموردون' },
  team:           { en: 'Team',           ar: 'الفريق' },
  reports:        { en: 'Reports',        ar: 'التقارير' },
  notifications:  { en: 'Notifications',  ar: 'الإشعارات' },
  settings:       { en: 'Settings',       ar: 'الإعدادات' },
  profile:        { en: 'Profile',        ar: 'الملف الشخصي' },
  boq:            { en: 'BOQ',            ar: 'جدول الكميات' },
}

const HIDE_ON_PATHS = ['/login', '/forgot-password', '/reset-password', '/']

export default function Breadcrumbs() {
  const location = useLocation()
  const { i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const { overrides } = useBreadcrumbContext()

  // Hide breadcrumbs on auth pages and dashboard
  if (HIDE_ON_PATHS.includes(location.pathname) || location.pathname === '/dashboard') {
    return null
  }

  // Split path into segments, ignoring empty
  const segments = location.pathname.split('/').filter(Boolean)
  if (segments.length === 0) return null

  const Sep = isRTL ? ChevronLeft : ChevronRight

  // Build cumulative paths for each segment
  const crumbs = segments.map((seg, idx) => {
    const path = '/' + segments.slice(0, idx + 1).join('/')
    const isLast = idx === segments.length - 1

    // Detect numeric IDs and slugs (e.g. /suppliers/3, /clients/sample-client)
    const isId = /^\d+$/.test(seg)
    const known = SEGMENT_LABELS[seg]
    const overridden = overrides[path]

    let label: string
    if (overridden) {
      // Detail page registered a friendly label (e.g. tender_code,
      // project_code) — prefer it over the auto-generated "#539".
      label = overridden
    } else if (known) {
      label = isRTL ? known.ar : known.en
    } else if (isId) {
      label = `#${seg}`
    } else {
      // Decode URL-encoded slug, replace dashes with spaces.
      label = decodeURIComponent(seg).replace(/-/g, ' ')
    }

    return { path, label, isLast }
  })

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 px-6 py-3 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <Link
        to="/dashboard"
        className="flex items-center gap-1 hover:text-blue-600 transition-colors"
      >
        <Home className="w-3.5 h-3.5" />
        <span>{isRTL ? 'الرئيسية' : 'Home'}</span>
      </Link>
      {crumbs.map((c) => (
        <span key={c.path} className="flex items-center gap-1.5">
          <Sep className="w-3.5 h-3.5 text-slate-400" />
          {c.isLast ? (
            <span className="font-medium text-slate-900 dark:text-white">
              {c.label}
            </span>
          ) : (
            <Link
              to={c.path}
              className="hover:text-blue-600 transition-colors capitalize"
            >
              {c.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  )
}